import TopNav from "ui/dashboard/topnav";
import { useContext, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { AuthContext, User } from "context/AuthContext";
import clsx from "clsx";
import { SvgPreviousPage, SvgNextPage } from "ui/svgs";
import { SignalContext } from "context/SignalContext";
import { ActRequestType } from "webrtc-player/common/base";
import { Fetcher } from "swr";
import { showToast } from "ui/components/common";
import { SvgToastInfo } from "ui/svgs";
import { fetchWithParams } from "lib/fetcher";
import {
  SvgBoxChecked,
  SvgBoxIndeterminateChecked,
  SvgBoxUnChecked,
} from "ui/svgs";
import { DeviceNvrsRes } from "lib/types";
import { Tooltip } from "react-tooltip";
import { ConfirmModalStyles } from "lib/constants";
import { useDebounce } from "use-debounce";
import TomlDownloader from "./components/tomlDownloader";
import BandwidthTest from "./components/bandwidthTest";
import useUncachedSWR from "lib/uncachedSWR";
import { Button, Select } from "antd";
import UpgradeSelect from "./components/upgradeSelect";
import Forward from "./components/forward";
import Cookies from "js-cookie";
import { loginForAdmin } from "../user/service";
import { DataControlerContext } from "context/DataControlerContext";
import DataControllerManager from "lib/dataControler";
import { useNavigate } from "react-router-dom";
import RefreshCloudSetting from "./components/refreshCloudSetting";
import DiskFormat from "./components/diskFormat";
import ExecCmd from "./components/execCmd";
import UpgradeCamera from "./components/upgradeCamera";
import AdminPassword from "./components/adminPassword";

export default function Page() {
  const { user } = useContext(AuthContext);
  const { signal, wsIsOpen, sessionId } = useContext(SignalContext);
  const [current, setCurrent] = useState(0);
  const [inputPage, setInputPage] = useState<string>("1");
  const [totalPage, setTotalPage] = useState(0);
  const [selectDevs, setSelectDevs] = useState<
    {
      id: number;
      deviceId: string;
      macAddress: string;
      upgrade: { upgradable: string };
    }[]
  >([]);

  const [autoRefreshing, setAutoRefreshing] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [upgradeBtnDisabled, setUpgradeBtnDisabled] = useState(true);
  const [rebootBtnDisabled, setRebootBtnDisabled] = useState(true);
  const [nextPageDisabled, setNextPageDisabled] = useState(true);
  const [lastPageDisabled, setLastPageDisabled] = useState(true);
  const [upgradeConfirmModalOpen, setUpgradeConfirmModalOpen] = useState(false);
  const [selectUpgradeVisible, setSelectUpgradeVisible] = useState(false);
  const [rebootNvrConfirmModalOpen, setRebootNvrConfirmModalOpen] =
    useState(false);
  const [rebootEdgeConfirmModalOpen, setRebootEdgeConfirmModalOpen] =
    useState(false);
  const [trigger, setTrigger] = useState(false);
  const tbody = useRef<HTMLTableSectionElement>();
  const [serialNumber, setSerialNumber] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [siteName, setSiteName] = useState("");
  const [publicIp, setPublicIp] = useState("");
  const [diskState, setDiskState] = useState("");
  const [customerName, setCustomerName] = useState("");
  const pageSize = 10;
  const fetcher: Fetcher<DeviceNvrsRes, string> = ([url, token, orgId]) =>
    fetchWithParams(url, token, orgId);
  const [debouncedSerialNumber] = useDebounce(serialNumber, 500);
  const [debouncedDeviceId] = useDebounce(deviceId, 500);
  const { getControllerManagerWithWaiter } = useContext(DataControlerContext);
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);
  const pageInputRef = useRef<HTMLInputElement>();
  const { data, error, isLoading } = useUncachedSWR(
    [
      `/device/nvr/nvrs/admin?page.offset=${current}&page.limit=${pageSize}&showAll=${showAll}&sn=${debouncedSerialNumber}&device_id=${debouncedDeviceId}&siteName=${encodeURIComponent(siteName)}&publicIp=${publicIp}&customerName=${customerName}&fsDiskState=${diskState || ""}&ordering.by=device_id`,
      user.token,
      0,
      trigger,
    ],
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: true,
      refreshInterval: autoRefreshing ? 10000 : 0,
    },
  );

  useEffect(() => {
    setCurrent(0);
  }, [
    debouncedSerialNumber,
    debouncedDeviceId,
    showAll,
    siteName,
    publicIp,
    diskState,
    customerName,
  ]);

  useEffect(() => {
    if (pageInputRef.current) {
      pageInputRef.current.value = (current + 1).toString();
    }
  }, [current]);

  function doRefresh() {
    setTrigger((state) => !state);
  }
  function updatePageBtnState(page) {
    if (page === 0) {
      setLastPageDisabled(true);
    } else {
      setLastPageDisabled(false);
    }
    if (page + 1 === totalPage) {
      setNextPageDisabled(true);
    } else {
      setNextPageDisabled(false);
    }
  }

  function upgrade() {
    if (!signal || !wsIsOpen) {
      showToast(
        1,
        "There was a problem upgrading the nvr. Please refresh and try again.",
      );
      return;
    }
    selectDevs?.forEach((dev) => {
      const message: ActRequestType = {
        request: {
          id: sessionId,
          act: "device.nvr.upgrade",
          data: {
            nvrUpgrade: {
              id: dev.id,
            },
          },
        },
        headers: { orgId: user.orgId },
      };
      console.log("nvr upgrade message = ", message);
      showToast(0, "Upgrade message sent.", { minWidth: 200 });
      signal.sendMessage(message);
    });
  }

  function reboot(service: string) {
    if (!signal || !wsIsOpen) {
      showToast(
        1,
        "There was a problem rebooting the nvr. Please refresh and try again.",
      );
      return;
    }
    selectDevs.forEach((dev) => {
      const message: ActRequestType = {
        request: {
          id: sessionId,
          act: "device.reboot",
          data: {
            deviceReboot: {
              id: dev.deviceId,
              macAddress: dev.macAddress,
              service: service,
            },
          },
        },
        headers: { orgId: user.orgId },
      };
      console.log("nvr reboot message = ", message);
      showToast(0, "Restart message sent.", { minWidth: 200 });
      signal.sendMessage(message);
    });
  }

  function changePage(page) {
    if (page < 0 || page >= totalPage || !Number.isInteger(page)) {
      showToast(1, "Please enter the correct page number");
      return;
    }
    setUpgradeBtnDisabled(true);
    setRebootBtnDisabled(true);
    setSelectDevs([]);
    setCurrent(page);
  }

  useEffect(() => {
    if (data) {
      setTotalPage(Math.ceil(data?.page?.total / pageSize) || 0);
      const newSelectDev = [];
      data?.data?.forEach((dev) => {
        if (
          dev.status === "online" &&
          selectDevs.find((d) => d.id === dev.id)
        ) {
          newSelectDev.push(dev);
        }
      });
      setSelectDevs(newSelectDev);
    }
  }, [data]);

  useEffect(() => {
    updatePageBtnState(current);
  }, [current, totalPage]);

  useEffect(() => {
    const cannotUpgradeIndex = selectDevs?.findIndex(
      (dev) => dev?.upgrade?.upgradable !== "need_upgrade",
    );
    setUpgradeBtnDisabled(selectDevs.length === 0 || cannotUpgradeIndex >= 0);
    setRebootBtnDisabled(selectDevs.length === 0);
  }, [selectDevs]);
  const loginAsUser = async (userId) => {
    const res = await loginForAdmin(userId);
    if (res?.status !== 200) {
      showToast(1, "Failed to login as user, please try again later");
      return;
    }
    const token = res?.data.token;
    const accountName = res?.data.user.organization.name;
    Cookies.set("orgId", res?.data.user?.organization?.id, { expires: 1 });
    Cookies.set("userId", res?.data.user?.id, { expires: 1 });
    Cookies.set("token", token, {
      expires: 1,
      domain: "." + window.location.hostname,
    });
    Cookies.set("accountName", accountName, { expires: 1 });
    Cookies.set(
      "enableMonitoring",
      res?.data.user.organization.enableMonitoring,
      {
        expires: 1,
      },
    );
    Cookies.set(
      "name",
      res?.data.user.firstName + " " + res?.data.user.lastName,
    );
    Cookies.set("phoneNumber", res?.data.user.phoneNumber);
    window.parent.postMessage({ token: token, type: "loginClick" }, "*");
    const user: User = {
      id: res?.data.user.id,
      email: res?.data.user.email,
      accountName: res?.data.user.organization.name,
      orgId: res?.data.user.organization.id,
      role: res?.data.user.role.name,
      token: res?.data.token,
      name: res?.data.user.firstName + " " + res?.data.user.lastName,
      phoneNumber: res?.data.user.phoneNumber,
      organization: res?.data.user.organization,
    };
    DataControllerManager.orgId = res?.data.user?.organization?.id;
    getControllerManagerWithWaiter().then((manager) => {
      manager.clearDataControllers();
    });
    setUser(user);

    navigate("/dashboard");
    window.location.reload();
  };
  if (error) {
    return <div>Something Error</div>;
  }
  return (
    <>
      <TopNav title="Upgrade" />
      <div className="flex-grow pr-6 pb-6">
        <div className="w-full h-full p-6 rounded-3xl bg-white">
          <div className="p-0">
            <div style={{ display: "flex", flexDirection: "row" }}>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setAutoRefreshing(!autoRefreshing);
                  }}
                >
                  {autoRefreshing ? (
                    <SvgBoxChecked
                      width="18"
                      className="fill-neutral-500 mr-1"
                    />
                  ) : (
                    <SvgBoxUnChecked
                      width="18"
                      className="fill-neutral-500 mr-1"
                    />
                  )}
                </button>
                <span className="text-sm">Auto Refresh</span>
                <button
                  onClick={() => {
                    setShowAll(!showAll);
                  }}
                >
                  {!showAll ? (
                    <SvgBoxChecked
                      width="18"
                      className="fill-neutral-500 mr-1"
                    />
                  ) : (
                    <SvgBoxUnChecked
                      width="18"
                      className="fill-neutral-500 mr-1"
                    />
                  )}
                </button>
                <span className="text-sm">Only Online</span>
              </div>
              <input
                disabled={Boolean(diskState)}
                placeholder="search device id"
                className="ml-3 w-[300px] rounded-md border border-gray-200 px-3 py-2 text-sm outline-2 placeholder:text-gray-500"
                onChange={(e) => setDeviceId(e.target.value)}
              />
              <input
                disabled={Boolean(diskState)}
                placeholder="search customer name"
                className="ml-3 w-[300px] rounded-md border border-gray-200 px-3 py-2 text-sm outline-2 placeholder:text-gray-500"
                onChange={(e) => setCustomerName(e.target.value)}
              />
              <input
                disabled={Boolean(diskState)}
                placeholder="search serial number"
                className="ml-3 w-[300px] rounded-md border border-gray-200 px-3 py-2 text-sm outline-2 placeholder:text-gray-500"
                onChange={(e) => setSerialNumber(e.target.value)}
              />
              <input
                disabled={Boolean(diskState)}
                placeholder="search site name"
                className="ml-3 w-[300px] rounded-md border border-gray-200 px-3 py-2 text-sm outline-2 placeholder:text-gray-500"
                onChange={(e) => setSiteName(e.target.value)}
              />
              <input
                disabled={Boolean(diskState)}
                placeholder="search public ip"
                className="ml-3 w-[300px] rounded-md border border-gray-200 px-3 py-2 text-sm outline-2 placeholder:text-gray-500"
                onChange={(e) => setPublicIp(e.target.value)}
              />
              <Select
                allowClear
                onChange={(e) => setDiskState(e)}
                className="ml-3 w-[300px] h-[54px]"
              >
                <Select.Option value="unknown">unknown</Select.Option>
                <Select.Option value="need_format">need_format</Select.Option>
                <Select.Option value="not_need_format">
                  not_need_format
                </Select.Option>
              </Select>
              <button
                className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                onClick={doRefresh}
              >
                Refresh
              </button>
            </div>
            <div className="overflow-x-auto w-full">
              <table className="min-w-[1200px] w-full overflow-hidden rounded-ld text-neutral-800 border-separate border-spacing-y-3">
                <thead>
                  <tr>
                    <th className="bg-neutral-100 text-sm px-4 py-4">
                      <button
                        onClick={() => {
                          if (
                            selectDevs.length ===
                            data?.data?.filter((dev) => dev.status === "online")
                              .length
                          ) {
                            setSelectDevs([]);
                          } else {
                            setSelectDevs(
                              data?.data?.filter(
                                (dev) => dev.status === "online",
                              ) || [],
                            );
                          }
                        }}
                      >
                        {selectDevs.length ===
                        data?.data?.filter((dev) => dev.status === "online")
                          .length ? (
                          <SvgBoxChecked
                            width="18"
                            className="fill-neutral-500 mr-1"
                          />
                        ) : selectDevs.length > 0 ? (
                          <SvgBoxIndeterminateChecked
                            width="18"
                            className="fill-neutral-500 mr-1"
                          />
                        ) : (
                          <SvgBoxUnChecked
                            width="18"
                            className="fill-neutral-500 mr-1"
                          />
                        )}
                      </button>
                    </th>
                    <th className="bg-neutral-100 text-sm px-4 py-4">
                      Device Id
                    </th>
                    <th className="bg-neutral-100 text-sm px-4 py-4">Action</th>
                    <th className="bg-neutral-100 text-sm px-8 py-4">Name</th>
                    <th className="bg-neutral-100 text-sm px-8 py-4">Model</th>
                    <th className="bg-neutral-100 text-sm px-8 py-4">
                      Site Name
                    </th>
                    <th className="bg-neutral-100 text-sm px-8 py-4">
                      Customer Name
                    </th>
                    <th className="bg-neutral-100 text-sm px-8 py-4">
                      Serial Number
                    </th>
                    <th className="bg-neutral-100 text-sm px-8 py-4">
                      Mac Address
                    </th>
                    <th className="bg-neutral-100 text-sm px-8 py-4">
                      Ip Address
                    </th>
                    <th className="bg-neutral-100 text-sm px-8 py-4">
                      Version
                    </th>
                    <th className="bg-neutral-100 text-sm px-8 py-4">
                      Claimed
                    </th>
                    <th className="bg-neutral-100 text-sm px-4 py-4">
                      Upgrade State
                    </th>
                    <th className="bg-neutral-100 text-sm px-4 py-4">
                      Upgradeable
                    </th>
                    <th className="bg-neutral-100 text-sm px-4 py-4">
                      Target Version
                    </th>
                    <th className="bg-neutral-100 text-sm px-4 py-4">
                      Disk State
                    </th>
                  </tr>
                </thead>
                <tbody ref={tbody}>
                  {data?.data?.map((e) => {
                    return (
                      <tr key={e.id}>
                        <td className="text-center">
                          <button
                            className={clsx(
                              "disabled:cursor-not-allowed cursor-pointer disabled:opacity-50",
                            )}
                            disabled={e.status !== "online"}
                            onClick={() => {
                              if (selectDevs.find((d) => d.id === e.id)) {
                                setSelectDevs(
                                  selectDevs?.filter((d) => d.id !== e.id),
                                );
                              } else {
                                setSelectDevs([...selectDevs, e]);
                              }
                            }}
                          >
                            {selectDevs.find((d) => d.id === e.id) ? (
                              <SvgBoxChecked
                                width="18"
                                className="fill-neutral-500 mr-1"
                              />
                            ) : (
                              <SvgBoxUnChecked
                                width="18"
                                className="fill-neutral-500 mr-1"
                              />
                            )}
                          </button>
                        </td>
                        <td className="text-center">{e.deviceId}</td>
                        <td className="text-center">
                          <TomlDownloader deviceItem={e} orgId={user.orgId} />
                          <BandwidthTest deviceItem={e} orgId={user.orgId} />
                          <Forward deviceId={e.id} orgId={e.orgId} />
                          <Button
                            type="link"
                            onClick={() => loginAsUser(e.dealerOwnerId)}
                          >
                            Login To DealerOwner(Id:{e.dealerOwnerId})
                          </Button>
                          <Button
                            type="link"
                            onClick={() => loginAsUser(e.customerOwnerId)}
                          >
                            Login To CustomerOwner(Id:{e.customerOwnerId})
                          </Button>
                          <RefreshCloudSetting deviceId={e.deviceId} nvr={e} />
                          <DiskFormat deviceId={e.deviceId} orgId={e.orgId} />
                          <ExecCmd deviceId={e.deviceId} orgId={e.orgId} />
                          <UpgradeCamera
                            deviceId={e.deviceId}
                            orgId={e.orgId}
                          />
                          <AdminPassword
                            deviceId={e.deviceId}
                            orgId={e.orgId}
                          />
                        </td>
                        <td className="text-center">
                          <span
                            className={clsx(
                              "inline-block w-2 h-2 rounded-full mr-1 relative -top-0.5",
                              e.status === "online"
                                ? "bg-teal-600"
                                : "bg-red-500",
                            )}
                          ></span>
                          {e.name}
                        </td>
                        <td className="text-center">{e.model}</td>
                        <td className="text-center">{e.siteName}</td>
                        <td className="text-center">{e.customerName}</td>
                        <td className="text-center">{e.sn}</td>
                        <td className="text-center">{e.macAddress}</td>
                        <td className="text-center">
                          {e.externalIp}
                          <br />
                          {e.ips?.at(0)}
                        </td>
                        <td className="text-center">
                          <p>
                            {e.upgrade?.services?.find(
                              (s) => s && s.service === "nvr",
                            )?.version || "-"}
                          </p>
                          <p>
                            <span className="text-zinc-500 text-sm mr-1">
                              Edge:
                            </span>
                            {e.upgrade?.services?.find(
                              (s) => s && s.service === "nvr_edge",
                            )?.version || "-"}
                          </p>
                          <p>
                            <span className="text-zinc-400 text-sm  mr-1">
                              Daemon:
                            </span>

                            {e.upgrade?.services?.find(
                              (s) => s && s.service === "nvr_daemon",
                            )?.version || "-"}
                          </p>
                        </td>
                        <td className="text-center">
                          {e?.isClaimed ? "Yes" : "No"}
                        </td>
                        <td className="text-center">
                          {e.upgrade?.upgradingState || "-"}
                          {e.upgrade?.upgradingState === "failed" && (
                            <span
                              className="inline-block ml-1 hover:bg-gray-100 hover-bg-gray-100 relative top-0.5"
                              data-tooltip-id="errorTooltip"
                              data-tooltip-content={e.upgrade?.error || "-"}
                            >
                              <Tooltip
                                id="errorTooltip"
                                className=" !text-white !rounded-xl !shadow-xl"
                              />
                              <SvgToastInfo
                                width="16"
                                className="fill-neutral-600"
                              />
                            </span>
                          )}
                        </td>

                        <td className="text-center">
                          {e.upgrade?.upgradable === "need_upgrade"
                            ? "Yes"
                            : "No"}
                        </td>

                        <td className="text-center">
                          <div>
                            {e.upgrade?.services?.find(
                              (s) => s && s.service === "nvr",
                            )?.targetVersion || "-"}
                          </div>
                          <div>
                            {e.upgrade?.services?.find(
                              (s) => s && s.service === "nvr_edge",
                            )?.targetVersion || "-"}
                          </div>
                        </td>
                        <td className="text-center">
                          <div style={{ width: "200px" }}>
                            Free:{e.diskState?.fsDiskState.free || "-"} M
                            <br />
                            Total:{e.diskState?.fsDiskState.total || "-"} M
                            <br />
                            NeedFormat:
                            {e.diskState?.fsDiskState.needFormat.toString() ||
                              "-"}
                            <br />
                            {e.diskState?.localHdds?.map?.((hdd) => {
                              return (
                                <div key={hdd.id}>
                                  local hdd id:{hdd.id} <br />
                                  RemainCapacity:{hdd.remainCapacity} M
                                  <br />
                                  TotalCapacity:{hdd.totalCapacity} M
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {isLoading && (
                    <tr>
                      <td>
                        <div
                          className="ml-1 text-blue-500  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                          role="status"
                        ></div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex item-center justify-end mr-4 mt-2">
              <div className="flex justify-end items-center text-zinc-600">
                <button
                  className="flex p-3 items-center justify-center rounded-full hover:bg-zinc-200 disabled:hover:bg-transparent"
                  disabled={lastPageDisabled}
                  onClick={() => {
                    changePage(current - 1);
                  }}
                >
                  <SvgPreviousPage
                    width="12"
                    className={clsx(
                      lastPageDisabled
                        ? "fill-zinc-800 opacity-25"
                        : "fill-zinc-500 opacity-100",
                    )}
                  />
                </button>
                <input
                  className="w-10 text-center border border-gray-200 rounded-md mx-2"
                  ref={pageInputRef}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ""); // 移除所有非数字字符
                    e.target.value = value;
                    console.log(value);
                    setInputPage(value || "");
                  }}
                  //value={inputPage}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      changePage((parseInt(inputPage) ?? 1) - 1);
                    }
                  }}
                />
                <button
                  className="flex p-3 items-center justify-center rounded-full hover:bg-zinc-200 disabled:hover:bg-transparent"
                  disabled={nextPageDisabled}
                  onClick={() => {
                    changePage(current + 1);
                  }}
                >
                  <SvgNextPage
                    width="11"
                    className={clsx(
                      nextPageDisabled
                        ? "fill-zinc-800 opacity-25"
                        : "fill-zinc-500 opacity-100",
                    )}
                  />
                </button>
                <label className="font-medium">
                  {"Total Device: " + (data?.page?.total || "")}
                </label>
                <label className="font-medium ml-1">Total Page:</label>
                <label className="font-medium ml-1">{totalPage}</label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setUpgradeConfirmModalOpen(true);
                }}
                disabled={upgradeBtnDisabled}
                className="mt-2 flex items-center rounded-xl text-white px-5 py-3 text-base font-semibold bg-blue-500 disabled:bg-blue-500 transition-colors hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                upgrade
              </button>
              <button
                onClick={() => {
                  setSelectUpgradeVisible(true);
                }}
                disabled={selectDevs.length === 0}
                className="mt-2 flex items-center rounded-xl text-white px-5 py-3 text-base font-semibold bg-blue-500 disabled:bg-blue-500 transition-colors hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                upgrade with selection
              </button>
              <button
                onClick={() => {
                  setRebootNvrConfirmModalOpen(true);
                }}
                disabled={rebootBtnDisabled}
                className="mt-2 flex items-center rounded-xl text-white px-5 py-3 text-base font-semibold bg-blue-500 disabled:bg-blue-500 transition-colors hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                reboot nvr
              </button>
              <button
                onClick={() => {
                  setRebootEdgeConfirmModalOpen(true);
                }}
                disabled={rebootBtnDisabled}
                className="mt-2 flex items-center rounded-xl text-white px-5 py-3 text-base font-semibold bg-blue-500 disabled:bg-blue-500 transition-colors hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                reboot egde
              </button>
            </div>
          </div>
        </div>
        <UpgradeSelect
          visible={selectUpgradeVisible}
          onClose={() => setSelectUpgradeVisible(false)}
          selectedDevs={selectDevs}
        />
        <Modal
          isOpen={upgradeConfirmModalOpen}
          style={ConfirmModalStyles}
          contentLabel="Example Modal"
        >
          <h1 className="text-xl font-bold">About to upgrade this nvr</h1>
          <div className="text-base mt-4">
            Are you sure you want to upgrade this nvr?
          </div>
          <div className="float-right mt-6">
            <button
              className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={() => {
                setUpgradeConfirmModalOpen(false);
              }}
            >
              Cancel
            </button>
            <button
              className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={() => {
                upgrade();
                setUpgradeConfirmModalOpen(false);
              }}
            >
              Upgrade
            </button>
          </div>
        </Modal>
        <Modal
          isOpen={rebootNvrConfirmModalOpen}
          style={ConfirmModalStyles}
          contentLabel="Example Modal"
        >
          <h1 className="text-xl font-bold">About to reboot this nvr</h1>
          <div className="text-base mt-4">
            Are you sure you want to reboot this nvr?
          </div>
          <div className="float-right mt-6">
            <button
              className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={() => {
                setRebootNvrConfirmModalOpen(false);
              }}
            >
              Cancel
            </button>
            <button
              className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={() => {
                reboot("nvr");
                setRebootNvrConfirmModalOpen(false);
              }}
            >
              Reboot
            </button>
          </div>
        </Modal>
        <Modal
          isOpen={rebootEdgeConfirmModalOpen}
          style={ConfirmModalStyles}
          contentLabel="Example Modal"
        >
          <h1 className="text-xl font-bold">About to reboot this edge</h1>
          <div className="text-base mt-4">
            Are you sure you want to reboot this edge?
          </div>
          <div className="float-right mt-6">
            <button
              className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={() => {
                setRebootEdgeConfirmModalOpen(false);
              }}
            >
              Cancel
            </button>
            <button
              className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={() => {
                reboot("nvr_edge");
                setRebootEdgeConfirmModalOpen(false);
              }}
            >
              Reboot
            </button>
          </div>
        </Modal>
      </div>
    </>
  );
}
